import { useEffect, useRef } from 'react';

/** Strips any existing "(N)" or "(99+)" unread prefix from a title. */
const stripCountPrefix = (title) => title.replace(/^\(\d+\+?\)\s*/, '');

// ── Module-level singleton — same pattern as the admin ──────────────────────
const notificationSound = new Audio('/sounds/notification.mp3');
let lastPlayed = 0;

const playNotificationSound = () => {
  const now = Date.now();
  // Throttle: play at most once every 3 seconds
  if (now - lastPlayed > 3000) {
    lastPlayed = now;
    notificationSound.currentTime = 0;
    notificationSound.play().catch(err => {
      console.warn('Browser blocked autoplay of notification sound:', err);
    });
  }
};

/**
 * Keeps the browser tab title in sync with the unread count even when
 * metaUtils (or any other code) overwrites document.title on route changes.
 *
 * Works by observing the <title> element via MutationObserver and
 * re-applying the "(N)" prefix whenever it gets wiped.
 *
 * @param {number} unreadCount - Current total unread count
 */
export function useUnreadTitle(unreadCount) {
  const prevCountRef = useRef(0);
  // Tracks the "clean" title (without our count prefix) so we survive route changes
  const baseTitleRef = useRef(stripCountPrefix(document.title));

  // ── Watch for external title changes (route navigation, metaUtils, etc.) ──
  useEffect(() => {
    const titleEl = document.querySelector('title');
    if (!titleEl) return;

    const observer = new MutationObserver(() => {
      const current = document.title;
      const stripped = stripCountPrefix(current);

      // Something else changed the base title (route change, etc.)
      if (stripped !== stripCountPrefix(baseTitleRef.current)) {
        baseTitleRef.current = stripped;
      }

      // Re-apply our count prefix if it got wiped and we still have unread messages
      if (prevCountRef.current > 0 && !current.startsWith('(')) {
        document.title = `(${prevCountRef.current > 99 ? '99+' : prevCountRef.current}) ${stripped}`;
      }
    });

    observer.observe(titleEl, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  // ── Update title & play sound whenever unread count changes ─────────────────
  useEffect(() => {
    // Sound: only play when count INCREASES (new message arrived)
    if (unreadCount > prevCountRef.current) {
      playNotificationSound();
    }
    prevCountRef.current = unreadCount;

    // Capture the current base title (strip any stale prefix first)
    const base = stripCountPrefix(document.title);
    baseTitleRef.current = base;

    if (unreadCount > 0) {
      document.title = `(${unreadCount > 99 ? '99+' : unreadCount}) ${base}`;
    } else {
      document.title = base;
    }
  }, [unreadCount]);
}
